import { useEffect, useRef, useState } from 'react'

import {
  GetPlannedBreaksDocument,
  GetPlannedBreaksQuery,
  GetPlannedBreaksQueryVariables,
  PlannedBreakFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import PlannedBreaksCollection from 'modules/time_planning/store/internal/collections/plannedBreaks'

export type PlannedBreaks =
  GetPlannedBreaksQuery['timePlanning']['plannedBreaksConnection']['nodes']

interface Props {
  variables: Omit<GetPlannedBreaksQueryVariables, 'first' | 'after'>
  skip?: boolean
}

const PAGE_SIZE = 1000

export const useGetPlannedBreaks = ({ variables, skip = false }: Props) => {
  const { loading, data, refetch, fetchMore } = useQuery(GetPlannedBreaksDocument, {
    variables: { ...variables, first: PAGE_SIZE },
    skip,
    notifyOnNetworkStatusChange: true,
  })

  const pageInfo = data?.timePlanning.plannedBreaksConnection.pageInfo
  const draining = useRef(false)
  // When pagination fails (rejection or missing payload) we stop draining instead of
  // looping forever on a `hasNextPage: true` cursor that we can no longer advance.
  const [drainFailed, setDrainFailed] = useState(false)

  // Reset the failure latch whenever the underlying query identity changes so a
  // refetch / variable change can retry pagination cleanly.
  const variablesKey = JSON.stringify(variables)
  useEffect(() => {
    setDrainFailed(false)
  }, [variablesKey, skip])

  useEffect(() => {
    if (skip || drainFailed) return
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return
    if (draining.current) return

    draining.current = true
    fetchMore({
      variables: { after: pageInfo.endCursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous

        return {
          ...previous,
          timePlanning: {
            ...previous.timePlanning,
            plannedBreaksConnection: {
              ...fetchMoreResult.timePlanning.plannedBreaksConnection,
              nodes: [
                ...previous.timePlanning.plannedBreaksConnection.nodes,
                ...fetchMoreResult.timePlanning.plannedBreaksConnection.nodes,
              ],
            },
          },
        }
      },
    })
      .then((result) => {
        if (!result?.data) setDrainFailed(true)
      })
      .catch(() => {
        setDrainFailed(true)
      })
      .finally(() => {
        draining.current = false
      })
  }, [skip, drainFailed, pageInfo?.hasNextPage, pageInfo?.endCursor, fetchMore])

  const plannedBreaks: PlannedBreakFragment[] = data
    ? data.timePlanning.plannedBreaksConnection.nodes
    : []
  const plannedBreaksCollection = PlannedBreaksCollection.buildFromRequest(plannedBreaks)

  return {
    refetch,
    loading: loading || (Boolean(pageInfo?.hasNextPage) && !drainFailed),
    plannedBreaks,
    plannedBreaksCollection,
  }
}
